import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material';
import { Button } from '@components/buttons';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { CloudUploadIcon } from '@icons/CloudUploadIcon';
import { PlusIcon } from '@icons/PlusIcon';
import type { FileAttachment, PdfFilePreview } from '@customHooks/useFileDialog';
import { useFileDialog } from '@customHooks/useFileDialog';
import { DOCUMENT_FILE_FILTER, parseFileName } from '@utils/CommonUtil';
import { AttachmentFile } from './AttatchmentFile';

export type FileUploadProps = {
  attachments: FileAttachment[];
  disabledFileUpdate?: boolean;
  accept?: string;
  showFileCount?: boolean;
  maxFileSizeBytes?: number;
  updateAttachments: (attachments: FileAttachment[]) => void;
  updatePdfPreview?: (pdfPreview: PdfFilePreview) => void;
};

export const MAX_FILE_COUNT = 10;
export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50MB
const UPLOAD_DELAY_MS = 500;

/** accept 문자열에서 허용된 확장자 목록 파싱 */
const parseAcceptExtensions = (accept: string): string[] => {
  return accept
    .split(',')
    .map(ext => ext.trim().toLowerCase())
    .filter(ext => ext.startsWith('.'))
    .map(ext => ext.slice(1));
};

/** accept 문자열에 와일드카드(예: image/*)가 포함되어 있는지 확인 */
const hasWildcard = (accept: string, type: string): boolean => {
  return accept.split(',').some(ext => ext.trim().toLowerCase() === `${type}/*`);
};

/** 드래그 앤 드롭 시 파일이 accept 필터에 맞는지 검증 */
const isFileAccepted = (file: File, accept: string): boolean => {
  const extensions = parseAcceptExtensions(accept);
  const fileExt = file.name.split('.').pop()?.toLowerCase() || '';

  if (extensions.includes(fileExt)) return true;
  if (hasWildcard(accept, 'image') && file.type.startsWith('image/')) return true;
  if (hasWildcard(accept, 'video') && file.type.startsWith('video/')) return true;
  if (hasWildcard(accept, 'audio') && file.type.startsWith('audio/')) return true;

  return false;
};

export const FileUpload = ({
  attachments,
  disabledFileUpdate,
  accept,
  showFileCount = false,
  maxFileSizeBytes,
  updateAttachments,
  updatePdfPreview,
}: FileUploadProps) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const {
    uiStore: { toastStore },
  } = useStore();

  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  // 첨부파일 관리
  const { openFileDialog, dummyFiles, addDummy, setDummyFinished } = useFileDialog({
    fileLength: attachments.length,
    accept,
  });

  const uploadValidFiles = (validFileList: File[]) => {
    const filesToUpload = validFileList.slice(0, MAX_FILE_COUNT - attachments.length);

    // 모든 파일의 더미 생성
    const fileDummyPairs = filesToUpload.map(file => ({
      file,
      dummy: addDummy(file.name),
    }));

    // 모든 파일을 한 번에 처리
    setTimeout(() => {
      const newAttachments = fileDummyPairs.map(({ file, dummy }) => {
        const { fileName, fileExtension } = parseFileName(file.name);
        setDummyFinished(dummy.fileId);

        return {
          fileId: dummy.fileId,
          fileName,
          fileExtension,
          file,
        };
      });

      updateAttachments([...attachments, ...newAttachments]);
    }, UPLOAD_DELAY_MS);
  };

  // 파일 업로드 핸들러
  const handleAttachFileClick = () => {
    if (attachments.length >= MAX_FILE_COUNT) {
      toastStore.open({
        message: t('File_Upload.Max_Files_Message', { count: MAX_FILE_COUNT }),
      });
      return;
    }
    openFileDialog(handleFileSelection);
  };

  const handleFileSelection = (fileList: FileList) => {
    const files = Array.from(fileList);
    if (attachments.length + files.length > MAX_FILE_COUNT) {
      toastStore.open({
        message: t('File_Upload.Max_Files_Simple', { count: MAX_FILE_COUNT }),
      });
      return;
    }

    const fileSizeLimit = maxFileSizeBytes ?? MAX_FILE_SIZE_BYTES;
    const fileSizeLabelMB = Math.round(fileSizeLimit / (1024 * 1024));
    const validFileList = files.filter(({ size }) => size <= fileSizeLimit);
    if (validFileList.length !== files.length) {
      toastStore.open({
        message: t('File_Upload.File_Size_Limit', { size: `${fileSizeLabelMB}MB` }),
      });
    }
    if (validFileList.length === 0) return;

    uploadValidFiles(validFileList);
  };

  const handleDeleteFile = (fileId: string) => {
    updateAttachments(attachments.filter(file => file.fileId !== fileId));
  };

  const handlePdfPreview = (fileId: string) => {
    if (!updatePdfPreview) return;

    const attachment = attachments.find(file => file.fileId === fileId);
    if (attachment) {
      if (attachment.fileExtension.toLowerCase() !== 'pdf') {
        toastStore.open({
          message: t('File_Upload.PDF_Preview_Only'),
        });
        return;
      }

      if (!attachment.file) return;

      updatePdfPreview({
        open: true,
        file: attachment.file,
        fileName: `${attachment.fileName}.${attachment.fileExtension}`,
        fileId,
        numPages: 0,
      });
    }
  };

  // Drag & Drop 핸들러
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabledFileUpdate) return;
    dragCounterRef.current += 1;
    if (e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    dragCounterRef.current = 0;

    if (disabledFileUpdate) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;

    // accept 필터 수동 적용 (drag & drop은 브라우저의 accept 속성이 적용되지 않음)
    const acceptFilter = accept || DOCUMENT_FILE_FILTER;
    const acceptedFiles = droppedFiles.filter(file => isFileAccepted(file, acceptFilter));

    if (acceptedFiles.length === 0) {
      toastStore.open({ message: t('File_Upload.Invalid_File_Type') });
      return;
    }

    if (acceptedFiles.length !== droppedFiles.length) {
      toastStore.open({ message: t('File_Upload.Some_Files_Rejected') });
    }

    const dataTransfer = new DataTransfer();
    acceptedFiles.forEach(file => dataTransfer.items.add(file));
    handleFileSelection(dataTransfer.files);
  };

  // 빈 상태에서 드롭 영역 클릭 시 파일 다이얼로그 열기
  const handleDropZoneClick = () => {
    if (!disabledFileUpdate) {
      handleAttachFileClick();
    }
  };

  const isEmpty = attachments.length === 0 && dummyFiles.length === 0;

  return (
    <FileWrapper>
      <FileButtonWrapper>
        <Button
          type="button"
          variant="outline"
          color={colors['text-primary-light']}
          size="large"
          onClick={handleAttachFileClick}
          disabled={disabledFileUpdate}
          startIcon={<PlusIcon width={16} height={16} color={colors['ic-primary-light']} />}
        >
          {t('File_Upload.Attach_File_Register')}
          {showFileCount && <FileCount>{`(${attachments.length}/${MAX_FILE_COUNT})`}</FileCount>}
        </Button>
      </FileButtonWrapper>
      <DropZone
        $isEmpty={isEmpty}
        $isDragOver={isDragOver}
        $isDisabled={disabledFileUpdate}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={isEmpty ? handleDropZoneClick : undefined}
      >
        {isEmpty && !isDragOver && (
          <EmptyDropZone>
            <CloudUploadIcon width={36} height={36} color={colors['ic-gray-light']} />
            <DropZoneMainText>{t('File_Upload.Drag_To_Upload')}</DropZoneMainText>
            <DropZoneSubText>{t('File_Upload.Or_Click_To_Select')}</DropZoneSubText>
          </EmptyDropZone>
        )}

        {/* 드래그 오버 시 오버레이 */}
        {isDragOver && (
          <DragOverlay>
            <CloudUploadIcon width={36} height={36} color={colors['ic-primary-light']} />
            <DragOverlayText>{t('File_Upload.Drop_To_Upload')}</DragOverlayText>
          </DragOverlay>
        )}

        {/* 업로드 중인 더미 파일들 */}
        {!isDragOver &&
          dummyFiles.map(dummy => <AttachmentFile key={dummy.fileId} dummyFile={dummy} isDummy />)}

        {/* 업로드 완료된 파일들 */}
        {!isDragOver &&
          attachments.map(attachment => (
            <AttachmentFile
              key={attachment.fileId}
              attachment={attachment}
              showPdfPreviewFn={updatePdfPreview ? handlePdfPreview : undefined}
              deleteFileFn={handleDeleteFile}
              enabledDeleteFile={!disabledFileUpdate}
            />
          ))}
      </DropZone>
    </FileWrapper>
  );
};

const FileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FileButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  ${fonts.Button2}
  button {
    border-radius: 3px;
    width: 100%;
    border-color: ${({ theme }) => theme.colors['border-gray-light']};
  }
`;

const DropZone = styled.div<{
  $isEmpty?: boolean;
  $isDragOver?: boolean;
  $isDisabled?: boolean;
}>`
  position: relative;
  padding: 16px 18px;
  background-color: ${({ theme, $isDragOver }) =>
    $isDragOver ? theme.colors['bg-primary-lighter'] : theme.colors['bg-gray-lighter']};
  border: 2.5px dashed
    ${({ theme, $isDragOver }) =>
      $isDragOver ? theme.colors['border-primary'] : theme.colors['border-gray-lighter']};
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 160px;
  box-sizing: border-box;
  overflow-y: auto;
  align-items: ${({ $isEmpty, $isDragOver }) => ($isEmpty || $isDragOver ? 'center' : 'stretch')};
  justify-content: ${({ $isEmpty, $isDragOver }) =>
    $isEmpty || $isDragOver ? 'center' : 'flex-start'};
  cursor: ${({ $isEmpty, $isDisabled }) => ($isEmpty && !$isDisabled ? 'pointer' : 'default')};
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;
`;

const EmptyDropZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 0;
`;

const DropZoneMainText = styled.span`
  ${fonts.Headline9}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  margin-top: 8px;
`;

const DropZoneSubText = styled.span`
  ${fonts.Body6}
  color: ${({ theme }) => theme.colors['text-gray-light']};
`;

const DragOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const DragOverlayText = styled.span`
  ${fonts.Headline9}
  color: ${({ theme }) => theme.colors['text-primary']};
`;

const FileCount = styled.span`
  ${fonts.Body2}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
`;
